import { Pet } from '../types/Pet/types';

const DAWER_COMPANY = 'DAWER';
const FIELPET_COMPANIES = new Set(['', 'FIELPET', 'FIEL PET']);
const FREE_COVERAGE_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const parseDate = (value?: string | null): Date | null => {
  if (!value) return null;

  const normalizedValue = value.trim();
  const isoDateOnlyMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateOnlyMatch) {
    const [, year, month, day] = isoDateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const localDateMatch = normalizedValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (localDateMatch) {
    const [, day, month, year] = localDateMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const date = new Date(normalizedValue);
  return Number.isNaN(date.getTime()) ? null : date;
};

type CoverageDatePayload = {
  fechaContratacion?: string | null;
  fecha_contratacion?: string | null;
  fechaContratacionDawer?: string | null;
  fecha_contratacion_dawer?: string | null;
  fechaCoberturaInicio?: string | null;
  fecha_cobertura_inicio?: string | null;
  fechaInicioCobertura?: string | null;
  fecha_inicio_cobertura?: string | null;
  fechaCoberturaDesde?: string | null;
  fecha_cobertura_desde?: string | null;
  coberturaDesde?: string | null;
  cobertura_desde?: string | null;
  coberturaInicioAt?: string | null;
  cobertura_inicio_at?: string | null;
  fechaCoberturaFin?: string | null;
  fecha_cobertura_fin?: string | null;
  fechaFinCobertura?: string | null;
  fecha_fin_cobertura?: string | null;
  fechaCoberturaHasta?: string | null;
  fecha_cobertura_hasta?: string | null;
  coberturaHasta?: string | null;
  cobertura_hasta?: string | null;
  coberturaFinAt?: string | null;
  cobertura_fin_at?: string | null;
  fechaAlta?: string | null;
  fecha_alta?: string | null;
  createdAt?: string | null;
  created_at?: string | null;
};

type CoverageDatePet = Partial<CoverageDatePayload> & {
  id?: number | string | null;
  solicitud?: CoverageDatePayload | null;
};

const getCoverageStartValue = (
  pet?: CoverageDatePet | null,
): string | null | undefined =>
  pet?.fechaContratacion ??
  pet?.fecha_contratacion ??
  pet?.fechaContratacionDawer ??
  pet?.fecha_contratacion_dawer ??
  pet?.fechaCoberturaInicio ??
  pet?.fecha_cobertura_inicio ??
  pet?.fechaInicioCobertura ??
  pet?.fecha_inicio_cobertura ??
  pet?.fechaCoberturaDesde ??
  pet?.fecha_cobertura_desde ??
  pet?.coberturaDesde ??
  pet?.cobertura_desde ??
  pet?.coberturaInicioAt ??
  pet?.cobertura_inicio_at ??
  pet?.solicitud?.fechaContratacion ??
  pet?.solicitud?.fecha_contratacion ??
  pet?.solicitud?.fechaContratacionDawer ??
  pet?.solicitud?.fecha_contratacion_dawer ??
  pet?.solicitud?.fechaCoberturaInicio ??
  pet?.solicitud?.fecha_cobertura_inicio ??
  pet?.solicitud?.fechaInicioCobertura ??
  pet?.solicitud?.fecha_inicio_cobertura ??
  pet?.solicitud?.fechaCoberturaDesde ??
  pet?.solicitud?.fecha_cobertura_desde ??
  pet?.solicitud?.coberturaDesde ??
  pet?.solicitud?.cobertura_desde ??
  pet?.solicitud?.coberturaInicioAt ??
  pet?.solicitud?.cobertura_inicio_at ??
  pet?.fechaAlta ??
  pet?.fecha_alta ??
  pet?.createdAt ??
  pet?.created_at ??
  pet?.solicitud?.fechaAlta ??
  pet?.solicitud?.fecha_alta ??
  pet?.solicitud?.createdAt ??
  pet?.solicitud?.created_at;

const getCoverageEndValue = (
  pet?: CoverageDatePet | null,
): string | null | undefined =>
  pet?.fechaCoberturaFin ??
  pet?.fecha_cobertura_fin ??
  pet?.fechaFinCobertura ??
  pet?.fecha_fin_cobertura ??
  pet?.fechaCoberturaHasta ??
  pet?.fecha_cobertura_hasta ??
  pet?.coberturaHasta ??
  pet?.cobertura_hasta ??
  pet?.coberturaFinAt ??
  pet?.cobertura_fin_at ??
  pet?.solicitud?.fechaCoberturaFin ??
  pet?.solicitud?.fecha_cobertura_fin ??
  pet?.solicitud?.fechaFinCobertura ??
  pet?.solicitud?.fecha_fin_cobertura ??
  pet?.solicitud?.fechaCoberturaHasta ??
  pet?.solicitud?.fecha_cobertura_hasta ??
  pet?.solicitud?.coberturaHasta ??
  pet?.solicitud?.cobertura_hasta ??
  pet?.solicitud?.coberturaFinAt ??
  pet?.solicitud?.cobertura_fin_at;

const normalizeCompanyName = (company?: string | null) =>
  company?.trim().toUpperCase() ?? '';

const getCoverageTone = (daysRemaining: number) => {
  if (daysRemaining <= 10) return 'danger';
  if (daysRemaining <= 20) return 'warning';
  return 'normal';
};

export const COVERAGE_EXPIRED_ALERT = {
  header: 'Cobertura finalizada',
  message:
    'No podés realizar esta acción porque tu mascota no tiene una cobertura activa. Elegí un plan para volver a habilitarla.',
};

const buildCoverageStatus = (
  daysRemaining: number,
  endDate: Date | null,
  hasCoverageDate = true,
) => ({
  daysRemaining,
  progress: hasCoverageDate
    ? Math.round((daysRemaining / FREE_COVERAGE_DAYS) * 100)
    : 0,
  endDate,
  tone: hasCoverageDate ? getCoverageTone(daysRemaining) : 'normal',
  hasCoverageDate,
});

export const isDawerPet = (pet?: Pick<Pet, 'compania'> | null): boolean =>
  normalizeCompanyName(pet?.compania) === DAWER_COMPANY;

export const isFielpetPet = (pet?: Pick<Pet, 'compania'> | null): boolean =>
  FIELPET_COMPANIES.has(normalizeCompanyName(pet?.compania));

export const hasExpiredCoverage = (
  pet?: (Pick<Pet, 'compania'> & Partial<CoverageDatePet>) | null,
): boolean => {
  const coverage = getCoverageStatus(pet);
  return isDawerPet(pet) && coverage.hasCoverageDate && coverage.daysRemaining === 0;
};

export const getCoverageStatus = (
  pet?: Partial<CoverageDatePet> | null,
) => {
  const startDate = parseDate(getCoverageStartValue(pet));
  const backendEndDate = parseDate(getCoverageEndValue(pet));

  if (!startDate && !backendEndDate) {
    return buildCoverageStatus(FREE_COVERAGE_DAYS, null, false);
  }

  const endDate = backendEndDate ?? new Date(startDate as Date);

  if (!backendEndDate) {
    endDate.setDate(endDate.getDate() + FREE_COVERAGE_DAYS);
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  const daysRemaining = clamp(
    Math.ceil((endDate.getTime() - today.getTime()) / MS_PER_DAY),
    0,
    FREE_COVERAGE_DAYS,
  );

  return buildCoverageStatus(daysRemaining, endDate);
};
